/**
 * TRC-20 (TRON mainnet) address validation.
 *
 * Full syntactic + base58check cryptographic validation. No network calls.
 * A TRON address is 25 bytes: 1 prefix byte (0x41) + 20 ripemd160 hash bytes
 * + 4 checksum bytes = first 4 bytes of sha256(sha256(21-byte payload)).
 * Base58-encoded that yields a 34-character string starting with "T".
 */

const BASE58_ALPHA =
  '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function base58Decode(input: string): Uint8Array | null {
  const bytes: number[] = [];
  for (const ch of input) {
    const idx = BASE58_ALPHA.indexOf(ch);
    if (idx === -1) return null;
    let carry = idx;
    for (let i = 0; i < bytes.length; i++) {
      carry += bytes[i] * 58;
      bytes[i] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  // Leading '1' chars represent leading zero bytes.
  for (const ch of input) {
    if (ch !== '1') break;
    bytes.push(0);
  }
  return new Uint8Array(bytes.reverse());
}

async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const buf = await crypto.subtle.digest('SHA-256', data as BufferSource);
  return new Uint8Array(buf);
}

export type Trc20Validation = { valid: true } | { valid: false; reason: string };

export async function validateTrc20Address(
  address: string
): Promise<Trc20Validation> {
  const trimmed = (address || '').trim();
  if (!trimmed) return { valid: false, reason: 'Укажите адрес кошелька' };
  if (!trimmed.startsWith('T')) {
    return { valid: false, reason: 'Адрес TRC-20 должен начинаться с T' };
  }
  if (trimmed.length !== 34) {
    return { valid: false, reason: 'Длина адреса должна быть 34 символа' };
  }

  const decoded = base58Decode(trimmed);
  if (!decoded || decoded.length !== 25) {
    return { valid: false, reason: 'Недопустимые символы в адресе' };
  }
  if (decoded[0] !== 0x41) {
    return { valid: false, reason: 'Это не адрес TRON mainnet' };
  }

  const payload = decoded.slice(0, 21);
  const checksum = decoded.slice(21, 25);

  const hash = await sha256(await sha256(payload));
  for (let i = 0; i < 4; i++) {
    if (hash[i] !== checksum[i]) {
      return { valid: false, reason: 'Неверная контрольная сумма адреса' };
    }
  }

  return { valid: true };
}
